import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { useAISummary } from './useAISummary';
import { CallRecapSummaryTab } from './CallRecapSummaryTab';
import { CallRecapTranscriptTab } from './CallRecapTranscriptTab';
import { formatDurationFromSeconds } from '../../../utils/dateUtils';
import { DateTime } from 'luxon';
import { ButtonSimple, FocusRing, IconNext } from '@momentum-ui/react-collaboration';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';

export type RecapTab = 'summary' | 'transcript';

interface ICallRecapModalProps {
  isOpen: boolean;
  onClose: () => void;
  containerId: string;
  callerName: string;
  startTime: string;
  durationSeconds: number;
  initialTab?: RecapTab;
}

export const CallRecapModal: React.FC<ICallRecapModalProps> = ({
  isOpen,
  onClose,
  containerId,
  callerName,
  startTime,
  durationSeconds,
  initialTab = 'summary',
}) => {
  const { t, i18n } = useTranslation('WebexCallHistory');
  const locale = i18n.resolvedLanguage || i18n.language;
  const [activeTab, setActiveTab] = useState<RecapTab>(initialTab);
  const { summaryData, loading } = useAISummary(containerId);
  const duration = formatDurationFromSeconds(durationSeconds || 0, locale);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const summaryTabRef = useRef<HTMLButtonElement>(null);
  const transcriptTabRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const callerId = useId();
  const metaId = useId();
  const summaryTabId = useId();
  const transcriptTabId = useId();
  const summaryPanelId = useId();
  const transcriptPanelId = useId();

  const { colors } = useTheme();

  const W = {
    overlay: {
      position: 'fixed' as const,
      inset: 0,
      zIndex: 9999,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'stretch',
      background: 'rgba(0,0,0,0.5)',
    },
    window: {
      display: 'flex',
      flexDirection: 'column' as const,
      width: '100%',
      height: '100%',
      overflow: 'hidden' as const,
      background: colors.backgroundPrimary,
    },
    headerBlock: {
      background: colors.backgroundSecondary,
      borderBottom: `1px solid ${colors.outlineSecondary}`,
      flexShrink: 0,
      padding: '0 12px',
      position: 'relative' as const,
      zIndex: 1,
    },
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '6px 0 2px',
    },
    titleText: {
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: '18px',
      letterSpacing: '0px',
      color: colors.textSecondary,
    },
    closeBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 40,
      height: 32,
      padding: 0,
      background: 'transparent',
      border: 'none',
      borderRadius: 4,
      cursor: 'pointer',
      color: colors.textSecondary,
    },
    caller: {
      fontWeight: 600,
      fontSize: '20px',
      lineHeight: '30px',
      letterSpacing: '0px',
      color: colors.textPrimary,
      margin: '2px 0 0',
    },
    meta: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '22px',
      letterSpacing: '0px',
      color: colors.textSecondary,
      margin: '2px 0 8px',
    },
    tabBar: {
      display: 'flex',
      gap: 8,
      padding: '10px 12px',
      flexShrink: 0,
    },
    tab: {
      padding: '4px 16px',
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '22px',
      letterSpacing: '0px',
      color: colors.textSecondary,
      background: 'transparent',
      border: '1px solid transparent', // preserve box size so switching states does not shift layout
      borderRadius: 16,
      cursor: 'pointer',
      outline: 'none',
    },
    tabActive: {
      padding: '4px 16px',
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '22px',
      letterSpacing: '0px',
      color: colors.textPrimary,
      background: colors.backgroundSecondaryActive,
      border: '1px solid transparent', // borderless pill; selection conveyed by background + text color
      borderRadius: 16,
      cursor: 'pointer',
      outline: 'none',
    },
    body: {
      flex: 1,
      overflow: 'hidden' as const,
      padding: '16px 12px',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column' as const,
    },
  };

  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);

  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const previousFocus = previousFocusRef.current;
      if (previousFocus && document.contains(previousFocus)) {
        previousFocus.focus();
      }
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;

    const activeTabRef = activeTab === 'summary' ? summaryTabRef : transcriptTabRef;
    const timer = window.setTimeout(() => activeTabRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, activeTab]);

  const moveToTab = useCallback((tab: RecapTab) => {
    setActiveTab(tab);
  }, []);

  const handleTabKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        moveToTab(activeTab === 'summary' ? 'transcript' : 'summary');
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        moveToTab(activeTab === 'summary' ? 'transcript' : 'summary');
        break;
      case 'Home':
        event.preventDefault();
        moveToTab('summary');
        break;
      case 'End':
        event.preventDefault();
        moveToTab('transcript');
        break;
      default:
        break;
    }
  }, [activeTab, moveToTab]);

  const fmtDate = (s: string) => {
    if (!s) return '';
    return DateTime.fromISO(s).setLocale(locale).toLocaleString(DateTime.DATE_SHORT);
  };
  const fmtTime = (s: string) => {
    if (!s) return '';
    return DateTime.fromISO(s).setLocale(locale).toLocaleString(DateTime.TIME_SIMPLE);
  };

  if (!isOpen) return null;

  return (
    <FocusLock returnFocus={false} autoFocus={false}>
      <div
        style={W.overlay}
        data-testid="call-recap-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${titleId} ${callerId}`}
        aria-describedby={metaId}
      >
        <div style={W.window}>
          {/* Header block: title + caller + meta, all same bg */}
          <div style={W.headerBlock}>
            <div style={W.titleRow}>
              <span id={titleId} style={W.titleText}>
                {t('aiSummary.recapTitle', { defaultValue: 'Call recaps' })}
              </span>
              <ButtonSimple
                style={W.closeBtn}
                aria-label={t('aiSummary.closeRecaps', { defaultValue: 'Close call recaps' })}
                title={t('aiSummary.close', { defaultValue: 'Close' })}
                onPress={onClose}
              >
<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </ButtonSimple>
            </div>
            <p id={callerId} style={W.caller}>{callerName}</p>
            <p id={metaId} style={W.meta}>{fmtDate(startTime)}, {fmtTime(startTime)}, {duration}</p>
          </div>

          {/* Tabs */}
          <div
            style={W.tabBar}
            role="tablist"
            aria-label={t('aiSummary.recapViews', { defaultValue: 'Call recap views' })}
            aria-orientation="horizontal"
          >
            <FocusRing>
              <button
                ref={summaryTabRef}
                id={summaryTabId}
                type="button"
                style={activeTab === 'summary' ? W.tabActive : W.tab}
                onClick={() => moveToTab('summary')}
                onKeyDown={handleTabKeyDown}
                role="tab"
                aria-selected={activeTab === 'summary'}
                aria-controls={summaryPanelId}
                tabIndex={activeTab === 'summary' ? 0 : -1}
              >
                {t('aiSummary.callSummaryTab', { defaultValue: 'Call summary' })}
              </button>
            </FocusRing>
            <FocusRing>
              <button
                ref={transcriptTabRef}
                id={transcriptTabId}
                type="button"
                style={activeTab === 'transcript' ? W.tabActive : W.tab}
                onClick={() => moveToTab('transcript')}
                onKeyDown={handleTabKeyDown}
                role="tab"
                aria-selected={activeTab === 'transcript'}
                aria-controls={transcriptPanelId}
                tabIndex={activeTab === 'transcript' ? 0 : -1}
              >
                {t('aiSummary.transcriptTab', { defaultValue: 'Transcript' })}
              </button>
            </FocusRing>
          </div>

          {/* Body */}
          <div style={W.body}>
            {activeTab === 'summary' && (
              <div
                id={summaryPanelId}
                role="tabpanel"
                aria-labelledby={summaryTabId}
                style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
              >
                <CallRecapSummaryTab summaryData={summaryData} loading={loading} />
              </div>
            )}
            {activeTab === 'transcript' && (
              <div
                id={transcriptPanelId}
                role="tabpanel"
                aria-labelledby={transcriptTabId}
                style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, minHeight: 0 }}
              >
                <CallRecapTranscriptTab containerId={containerId} />
              </div>
            )}
          </div>
        </div>
      </div>
    </FocusLock>
  );
};
